// ClaudeCode: Share link redemption page — public, no auth required
// URL format: /share/{id}#{hexKey}
// The fragment (#hexKey) never leaves the browser
import { useState, useEffect } from 'react';
import { redeemShareLink } from '../api';
import { importShareKey, decrypt, copyToClipboard } from '../crypto';

interface ShareRedeemProps {
  linkId: string;
}

export function ShareRedeem({ linkId }: ShareRedeemProps) {
  const [decryptedValue, setDecryptedValue] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function redeem() {
      try {
        // Get the key from the URL fragment
        const keyHex = window.location.hash.slice(1);
        if (!keyHex || keyHex.length !== 64) {
          setError('Invalid or missing decryption key in URL');
          setLoading(false);
          return;
        }

        // Fetch ciphertext from server
        const { encryptedPayload } = await redeemShareLink(linkId);

        // Import the share key and decrypt
        const shareKey = await importShareKey(keyHex);
        const plaintext = await decrypt(encryptedPayload, shareKey);

        setDecryptedValue(plaintext);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to retrieve shared secret');
      } finally {
        setLoading(false);
      }
    }

    redeem();
  }, [linkId]);

  const handleCopy = () => {
    if (decryptedValue) {
      copyToClipboard(decryptedValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 24,
      }}
    >
      <div
        style={{
          background: '#18181b',
          border: '1px solid #27272a',
          borderRadius: 12,
          padding: 32,
          width: '100%',
          maxWidth: 500,
        }}
      >
        <h2
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: '#fafafa',
            textAlign: 'center',
            marginBottom: 8,
          }}
        >
          Shared Secret
        </h2>
        <p
          style={{
            fontSize: 13,
            color: '#71717a',
            textAlign: 'center',
            marginBottom: 24,
          }}
        >
          This link is single-use and will expire. Copy the value now.
        </p>

        {loading && (
          <div style={{ textAlign: 'center', color: '#71717a' }}>Decrypting...</div>
        )}

        {error && (
          <div
            style={{
              background: '#450a0a',
              border: '1px solid #991b1b',
              borderRadius: 8,
              padding: 16,
              color: '#fca5a5',
              fontSize: 14,
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}

        {decryptedValue && (
          <>
            <div
              style={{
                background: '#09090b',
                border: '1px solid #27272a',
                borderRadius: 8,
                padding: 16,
                marginBottom: 16,
              }}
            >
              <pre
                style={{
                  fontFamily: 'monospace',
                  fontSize: 14,
                  color: '#fafafa',
                  wordBreak: 'break-all',
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                }}
              >
                {decryptedValue}
              </pre>
            </div>

            <button
              onClick={handleCopy}
              style={{
                width: '100%',
                padding: '10px 0',
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </button>

            <p
              style={{
                fontSize: 12,
                color: '#52525b',
                textAlign: 'center',
                marginTop: 12,
              }}
            >
              Clipboard auto-clears in 30 seconds
            </p>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <a
            href="/"
            style={{ fontSize: 13, color: '#71717a', textDecoration: 'none' }}
          >
            LifeSpace Vault
          </a>
        </div>
      </div>
    </div>
  );
}
